import { setIn } from 'icepick';
import isEqual from 'lodash/isEqual';
import { ff } from '@atlassian/jira-feature-flagging';

import { DEFAULT_ITEM_TYPE } from '../../common/constants';
import type { Field } from '../../common/constants/fields';
import { type SupportedThirdParty, ASANA, MONDAY } from '../../common/constants/import-type';
import type {
	AvailableJiraFields,
	ImportFieldMappings,
	ItemsConfig,
	ThirdPartyEntityStatusesState,
	ThirdPartyFieldConfigState,
	ThirdPartyFieldMapping,
	ThirdPartyFieldMappingsState,
	ThirdPartyHierarchyMapping,
	ThirdPartyImportSettings,
} from '../../common/types';
import type { ImportAdditionalProps } from './types';

const ASANA_PREFIX = '(Asana) ';
const MONDAY_PREFIX = '(monday) ';

const getPrefix = (thirdParty?: SupportedThirdParty) => {
	switch (thirdParty) {
		case ASANA:
			return ASANA_PREFIX;
		case MONDAY:
			return MONDAY_PREFIX;
		default:
			return '';
	}
};

const generateKey = (field: Field) => `${field.fieldType}-${field.fieldName}`;

type FieldNameTypeSet = Set<string>;

const generateExistingFieldNameTypeSet = (availableJiraFields: AvailableJiraFields) =>
	new Set(
		Object.keys(availableJiraFields).flatMap((type) => availableJiraFields[type].map(generateKey)),
	);

const generateNewFieldName = (
	existingFieldName: string,
	count: number,
	thirdParty?: SupportedThirdParty,
) =>
	count === 0
		? `${getPrefix(thirdParty)}${existingFieldName}`
		: `${getPrefix(thirdParty)}${existingFieldName} ${count}`;
/*
   Follows the following logic:
   - If there are no existing fields with the same name -> make no changes to the field name
   - If there is one existing field with the same name -> change field name to "(Asana) <asana-field-name>"
   - If there are n existing fields with the same name -> change field name to "(Asana) <asana-field-name> n-1"

   In all cases, add the processed asana field to the fieldNameTypeSet to ensure that subsequent asana fields with
   the same name will follow the logic above.
 */
export const generateFieldWithUniqueName = (
	field: Field,
	fieldNameTypeSet: FieldNameTypeSet,
	count = 0,
	thirdParty?: SupportedThirdParty,
): Field => {
	const key = generateKey(field);
	if (!fieldNameTypeSet.has(key)) {
		fieldNameTypeSet.add(key);
		return field;
	}

	const newField = setIn(
		field,
		['fieldName'],
		generateNewFieldName(field.fieldName, count, thirdParty),
	);
	const newKey = generateKey(newField);

	if (!fieldNameTypeSet.has(newKey)) {
		fieldNameTypeSet.add(newKey);
		return newField;
	}

	return generateFieldWithUniqueName(field, fieldNameTypeSet, count + 1, thirdParty);
};

const generateFieldMappingsForItem = (
	thirdPartyFieldMappings: ThirdPartyFieldMappingsState,
	availableJiraFieldsNameTypeSet: FieldNameTypeSet,
	updateAdditionalAttributes: (fieldMapping: ThirdPartyFieldMapping) => void,
	thirdParty?: SupportedThirdParty,
) => {
	const itemFieldMappings: ImportFieldMappings = {};
	const fieldNameTypeSet = new Set(availableJiraFieldsNameTypeSet);

	const getMappedField = (fieldMapping: ThirdPartyFieldMapping) => {
		if (!fieldMapping.mappingContext.isIncluded) return;
		if (fieldMapping.mappedJiraField.fieldId) {
			return fieldMapping.mappedJiraField;
		}

		// Ensure unique names for new custom fields
		return generateFieldWithUniqueName(
			fieldMapping.mappedJiraField,
			fieldNameTypeSet,
			0,
			thirdParty,
		);
	};

	Object.values(thirdPartyFieldMappings).forEach((fieldMapping: ThirdPartyFieldMapping) => {
		updateAdditionalAttributes(fieldMapping);
		const field = getMappedField(fieldMapping);
		if (field) {
			itemFieldMappings[fieldMapping.thirdPartyField.fieldId] = field;
		}
	});

	return itemFieldMappings;
};

type GetImportArgs = {
	state: ThirdPartyFieldConfigState;
	importSettings: ThirdPartyImportSettings;
	statusMapping?: ThirdPartyEntityStatusesState | null;
	thirdParty?: SupportedThirdParty;
};

export const getImportAdditionalProps = ({
	state,
	importSettings,
	statusMapping,
	thirdParty,
}: GetImportArgs): ImportAdditionalProps => {
	const availableJiraFieldsNameTypeSet = generateExistingFieldNameTypeSet(
		state.availableJiraFields,
	);
	let fieldMappings: ImportFieldMappings = {};
	const itemsConfig: ItemsConfig = {};
	const additionalAttributes = {
		attachmentsEnabled: importSettings.shouldImportAttachments,
		importUsersEnabled: importSettings.shouldImportUsers,
		importCommentsEnabled: importSettings.shouldImportComments,
		numFieldsTotal: 0,
		numFieldsExcluded: 0,
		numFieldsChangedFromDefault: 0,
		...(ff('jwm-x2c-hierarchy-mapping_kkjnd') && {
			defaultHierarchyItem: '',
			selectedHierarchyItem: '',
		}),
	};

	const updateAdditionalAttributes =
		(itemType: string, hierarchyMapping?: ThirdPartyHierarchyMapping) =>
		(fieldMapping: ThirdPartyFieldMapping) => {
			if (!fieldMapping.mappingContext.isIncluded) {
				additionalAttributes.numFieldsExcluded += 1;
				return;
			}
			additionalAttributes.numFieldsTotal += 1;

			if (!isEqual(fieldMapping.defaultMappedJiraField, fieldMapping.mappedJiraField)) {
				additionalAttributes.numFieldsChangedFromDefault += 1;
			}
			if (
				ff('jwm-x2c-hierarchy-mapping_kkjnd') &&
				hierarchyMapping &&
				itemType === (hierarchyMapping.itemType || DEFAULT_ITEM_TYPE) && // TODO: Remove DEFAULT_ITEM_TYPE once Asana API is updated to support itemsConfig
				fieldMapping.thirdPartyField.fieldId === hierarchyMapping.mappingFieldId
			) {
				additionalAttributes.defaultHierarchyItem =
					fieldMapping.defaultMappedJiraField?.fieldName || '';
				additionalAttributes.selectedHierarchyItem = fieldMapping.mappedJiraField.fieldName;
			}
		};

	Object.entries(state.fieldMappingState).forEach(([itemType, itemTypeFieldMapping]) => {
		const itemFieldMappings = generateFieldMappingsForItem(
			itemTypeFieldMapping.thirdPartyFieldMappings,
			availableJiraFieldsNameTypeSet,
			updateAdditionalAttributes(itemType, state.hierarchyMapping),
			thirdParty,
		);

		const itemStatusMappings =
			statusMapping && itemType in statusMapping.entityStatusMappings
				? statusMapping.entityStatusMappings[itemType].statusMappings
				: {};

		itemsConfig[itemType] = {
			itemTypeId: itemTypeFieldMapping.itemTypeId,
			fieldMappings: itemFieldMappings,
			...(ff('x2c-clickup-importer-multi-list') ? { statusMappings: itemStatusMappings } : {}),
		};

		// TODO: Remove this once Asana API is updated to support itemsConfig
		fieldMappings = { ...fieldMappings, ...itemFieldMappings };
	});

	return {
		additionalPayload: {
			fieldMappings,
			itemsConfig,
			importSettings,
		},
		additionalAttributes,
	};
};
